import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  inject,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription, fromEvent, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import type { NavigationPerformanceEventData } from './navigation-performance-tracking.types';
import { NavigationPerformanceCategory } from './navigation-performance-tracking.types';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';
import { Tracking } from './tracking.service';

@Component({
  standalone: false,
  selector: 'fl-navigation-performance-tracking',
  template: ` <ng-container /> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationPerformanceTrackingComponent
  implements OnInit, OnDestroy
{
  private t = inject(Tracking);
  private config = inject<TrackingConfig>(TRACKING_CONFIG);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private isInitialNavigation = true;
  private previousUrl = '';
  private routerSubscription = new Subscription();
  private navigationStartTime = 0;

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      // Apply sampling rate
      if (Math.random() > this.config.navigationPerformanceSamplingRate) {
        return;
      }

      this.setupNavigationTracking();
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  private setupNavigationTracking(): void {
    // Track navigation start time for SPA navigations
    this.routerSubscription.add(
      this.router.events
        .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
        .subscribe(() => {
          this.navigationStartTime = performance.now();
        }),
    );

    // Track navigation end and collect metrics
    this.routerSubscription.add(
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          switchMap(event => {
            if (this.isInitialNavigation) {
              // For initial page load, wait for the load event to complete
              // This ensures all timing metrics (loadEventEnd, domComplete, etc.) are available
              if (document.readyState === 'complete') {
                // Already loaded
                return of(event);
              }
              // Wait for load event and return the navigation event
              return fromEvent(window, 'load').pipe(
                take(1),
                map(() => event),
              );
            }

            // For SPA navigations, return immediately
            return of(event);
          }),
        )
        .subscribe(event => {
          // Use setTimeout to ensure all metrics are finalized
          setTimeout(() => {
            this.collectAndSendMetrics(event.url);
          }, 0);
        }),
    );
  }

  private collectAndSendMetrics(url: string): void {
    const data: NavigationPerformanceEventData = {
      category: this.isInitialNavigation
        ? NavigationPerformanceCategory.INITIAL_PAGE_LOAD
        : NavigationPerformanceCategory.ROUTE_CHANGE,
      totalTime: 0,
      backendTime: 0,
      frontendTime: 0,
      previousUrl: this.previousUrl,
    };

    // For initial page load, collect full Navigation Timing data
    if (this.isInitialNavigation && performance.getEntriesByType) {
      const navigationEntries = performance.getEntriesByType(
        'navigation',
      ) as readonly (PerformanceEntry & PerformanceNavigationTiming)[];

      if (navigationEntries.length > 0) {
        const navTiming = navigationEntries[0];
        const { type: navigationType } = navTiming;
        // Get absolute time origin for converting relative timestamps to absolute
        const { timeOrigin } = performance;

        // Calculate key timing metrics (ensure non-negative integer values in ms)
        data.totalTime = Math.round(
          Math.max(0, navTiming.loadEventEnd - navTiming.fetchStart),
        );
        data.backendTime = Math.round(
          Math.max(0, navTiming.responseEnd - navTiming.fetchStart),
        );
        data.frontendTime = Math.round(
          Math.max(0, navTiming.loadEventEnd - navTiming.responseEnd),
        );

        // Convert navigationType string to numeric code for backend compatibility
        const navigationTypeMap: Record<string, number> = {
          navigate: 0,
          reload: 1,
          back_forward: 2,
          prerender: 255,
        };

        // Add all Navigation Timing fields with absolute timestamps (rounded to ms)
        Object.assign(data, {
          redirectCount: navTiming.redirectCount,
          navigationType: navigationTypeMap[navigationType] ?? 0,
          navigationStart: Math.round(timeOrigin),
          fetchStart: Math.round(timeOrigin + navTiming.fetchStart),
          redirectStart: Math.round(timeOrigin + navTiming.redirectStart),
          redirectEnd: Math.round(timeOrigin + navTiming.redirectEnd),
          domainLookupStart: Math.round(
            timeOrigin + navTiming.domainLookupStart,
          ),
          domainLookupEnd: Math.round(timeOrigin + navTiming.domainLookupEnd),
          connectStart: Math.round(timeOrigin + navTiming.connectStart),
          connectEnd: Math.round(timeOrigin + navTiming.connectEnd),
          requestStart: Math.round(timeOrigin + navTiming.requestStart),
          responseStart: Math.round(timeOrigin + navTiming.responseStart),
          responseEnd: Math.round(timeOrigin + navTiming.responseEnd),
          domInteractive: Math.round(timeOrigin + navTiming.domInteractive),
          domContentLoadedEventStart: Math.round(
            timeOrigin + navTiming.domContentLoadedEventStart,
          ),
          domContentLoadedEventEnd: Math.round(
            timeOrigin + navTiming.domContentLoadedEventEnd,
          ),
          domComplete: Math.round(timeOrigin + navTiming.domComplete),
          loadEventStart: Math.round(timeOrigin + navTiming.loadEventStart),
          loadEventEnd: Math.round(timeOrigin + navTiming.loadEventEnd),
          unloadEventStart: Math.round(timeOrigin + navTiming.unloadEventStart),
          unloadEventEnd: Math.round(timeOrigin + navTiming.unloadEventEnd),
        });

        // Get first paint timing if available (convert to absolute and microseconds as integer)
        const paintEntries = performance.getEntriesByType('paint');
        const firstPaint = paintEntries.find(p => p.name === 'first-paint');
        if (firstPaint) {
          Object.assign(data, {
            firstPaint: Math.round((timeOrigin + firstPaint.startTime) * 1000),
          });
        }
      }
    } else if (!this.isInitialNavigation && this.navigationStartTime > 0) {
      // For SPA route changes, calculate the navigation duration (rounded to ms)
      const navigationEndTime = performance.now();
      const navigationDuration = Math.round(
        Math.max(0, navigationEndTime - this.navigationStartTime),
      );

      // For SPA navigations, we only have totalTime (no backend/frontend split available)
      data.totalTime = navigationDuration;
      data.backendTime = 0;
      data.frontendTime = navigationDuration;
    }

    // Send the tracking event
    this.t.track('page_performance_timings', data);

    // Update state for next navigation (use full URL with domain)
    this.previousUrl = window.location.href;
    this.isInitialNavigation = false;
  }
}
